import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

// Import all dashboard components
import CoverageOverview from './components/CoverageOverview';
import ShiftAssignmentGrid from './components/ShiftAssignmentGrid';
import EmployeeAvailabilityMatrix from './components/EmployeeAvailabilityMatrix';
import RealTimeAlertsPanel from './components/RealTimeAlertsPanel';
import SystemHealthIndicators from './components/SystemHealthIndicators';
import BulkActionToolbar from './components/BulkActionToolbar';
import AdvancedFilters from './components/AdvancedFilters';

const AdminScheduleManagementDashboard = () => {
  const navigate = useNavigate();
  const [selectedItems, setSelectedItems] = useState([]);
  const [filters, setFilters] = useState({});
  const [savedViews, setSavedViews] = useState([
    { name: 'Tägliche Übersicht', filters: { dateRange: { start: new Date()?.toISOString()?.split('T')?.[0], end: new Date()?.toISOString()?.split('T')?.[0] } } },
    { name: 'Wöchentliche Planung', filters: { dateRange: { start: new Date()?.toISOString()?.split('T')?.[0], end: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0] } } }
  ]);
  const [dashboardLayout, setDashboardLayout] = useState('quadrant'); // quadrant, list, grid
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Mock user authentication check
  useEffect(() => {
    const user = localStorage.getItem('user');
    if (!user) {
      navigate('/google-o-auth-login');
      return;
    }

    // Check if user has admin role
    const userData = JSON.parse(user);
    if (userData?.role !== 'Admin') {
      navigate('/employee-shift-calendar');
      return;
    }
  }, [navigate]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e?.ctrlKey || e?.metaKey) {
        switch (e?.key) {
          case 'a':
            e?.preventDefault();
            // Select all items logic
            break;
          case 'd':
            e?.preventDefault();
            handleBulkAction('duplicate', selectedItems);
            break;
          case 'f':
            e?.preventDefault();
            // Focus search/filter
            break;
        }
      }
      
      if (e?.key === 'Escape') {
        setSelectedItems([]);
      }
      
      if (e?.key === 'Delete' && selectedItems?.length > 0) {
        handleBulkAction('delete', selectedItems);
      }
      
      if (e?.key === 'F11') {
        e?.preventDefault();
        setIsFullscreen(!isFullscreen);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedItems, isFullscreen]);

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    console.log('Filters changed:', newFilters);
  };

  const handleSaveView = (view) => {
    setSavedViews([...savedViews, view]);
    console.log('View saved:', view);
  };

  const handleBulkAction = (actionId, items) => {
    console.log(`Bulk action ${actionId} on items:`, items);
    
    switch (actionId) {
      case 'assign':
        // Handle shift assignment
        break;
      case 'reassign':
        // Handle shift reassignment
        break;
      case 'duplicate':
        // Handle shift duplication
        break;
      case 'delete':
        // Handle shift deletion
        if (window.confirm(`Sind Sie sicher, dass Sie ${items?.length} Schicht(en) löschen möchten?`)) {
          setSelectedItems([]);
        }
        break;
      case 'template':
        // Handle template creation
        break;
      case 'export':
        // Handle export
        break;
      case 'optimize':
        // Handle AI optimization
        break;
      case 'notify':
        // Handle notifications
        break;
      default:
        console.log('Unknown action:', actionId);
    }
  };

  const handleClearSelection = () => {
    setSelectedItems([]);
  };

  const handleLayoutChange = (layout) => {
    setDashboardLayout(layout);
  };

  const quickActions = [
    {
      id: 'create-shift',
      label: 'Neue Schicht',
      icon: 'Plus',
      color: 'text-primary',
      action: () => navigate('/shift-creation-and-editing')
    },
    {
      id: 'import-schedule',
      label: 'Zeitplan importieren',
      icon: 'Upload',
      color: 'text-accent',
      action: () => console.log('Import schedule')
    },
    {
      id: 'generate-report',
      label: 'Bericht erstellen',
      icon: 'FileText',
      color: 'text-secondary',
      action: () => console.log('Generate report')
    },
    {
      id: 'settings',
      label: 'Einstellungen',
      icon: 'Settings',
      color: 'text-muted-foreground',
      action: () => navigate('/system-settings-and-configuration')
    }
  ];

  return (
    <div className={`min-h-screen bg-background ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}>
      {!isFullscreen && <Header />}
      <main className={`${!isFullscreen ? 'pt-16' : ''} p-6`}>
        <div className="max-w-full mx-auto space-y-6">
          {/* Header Section */}
          {!isFullscreen && <Breadcrumb />}
          
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="BarChart3" size={24} className="text-primary" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-foreground">Admin Dashboard</h1>
                  <p className="text-muted-foreground">Umfassende Personalplanung und Schichtkoordination</p>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              {/* Quick Actions */}
              {quickActions?.map((action) => (
                <Button
                  key={action?.id}
                  variant="ghost"
                  size="sm"
                  onClick={action?.action}
                  iconName={action?.icon}
                  iconPosition="left"
                  iconSize={16}
                  className="hidden lg:flex"
                >
                  {action?.label}
                </Button>
              ))}

              {/* Layout Controls */}
              <div className="flex items-center space-x-1 border border-border rounded-lg p-1">
                <Button
                  variant={dashboardLayout === 'quadrant' ? 'default' : 'ghost'}
                  size="icon"
                  onClick={() => handleLayoutChange('quadrant')}
                  className="w-8 h-8"
                >
                  <Icon name="Grid2X2" size={14} />
                </Button>
                <Button
                  variant={dashboardLayout === 'list' ? 'default' : 'ghost'}
                  size="icon"
                  onClick={() => handleLayoutChange('list')}
                  className="w-8 h-8"
                >
                  <Icon name="List" size={14} />
                </Button>
                <Button
                  variant={dashboardLayout === 'grid' ? 'default' : 'ghost'}
                  size="icon"
                  onClick={() => handleLayoutChange('grid')}
                  className="w-8 h-8"
                >
                  <Icon name="Grid3X3" size={14} />
                </Button>
              </div>

              {/* Fullscreen Toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsFullscreen(!isFullscreen)}
              >
                <Icon name={isFullscreen ? "Minimize2" : "Maximize2"} size={16} />
              </Button>
            </div>
          </div>

          {/* System Health Indicators */}
          <SystemHealthIndicators />

          {/* Advanced Filters */}
          <AdvancedFilters
            onFiltersChange={handleFiltersChange}
            onSaveView={handleSaveView}
            savedViews={savedViews}
          />

          {/* Bulk Action Toolbar */}
          <BulkActionToolbar
            selectedItems={selectedItems}
            onClearSelection={handleClearSelection}
            onBulkAction={handleBulkAction}
          />

          {/* Main Dashboard Content */}
          {dashboardLayout === 'quadrant' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Top Left - Coverage Overview */}
              <div className="h-[500px]">
                <CoverageOverview />
              </div>

              {/* Top Right - Shift Assignment Grid */}
              <div className="h-[500px]">
                <ShiftAssignmentGrid />
              </div>

              {/* Bottom Left - Employee Availability Matrix */}
              <div className="h-[500px]">
                <EmployeeAvailabilityMatrix />
              </div>

              {/* Bottom Right - Real-time Alerts Panel */}
              <div className="h-[500px]">
                <RealTimeAlertsPanel />
              </div>
            </div>
          )}

          {dashboardLayout === 'list' && (
            <div className="space-y-6">
              <div className="h-[400px]">
                <CoverageOverview />
              </div>
              <div className="h-[500px]">
                <ShiftAssignmentGrid />
              </div>
              <div className="h-[500px]">
                <EmployeeAvailabilityMatrix />
              </div>
              <div className="h-[400px]">
                <RealTimeAlertsPanel />
              </div>
            </div>
          )}

          {dashboardLayout === 'grid' && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              <div className="h-[400px]">
                <CoverageOverview />
              </div>
              <div className="h-[400px]">
                <ShiftAssignmentGrid />
              </div>
              <div className="h-[400px]">
                <EmployeeAvailabilityMatrix />
              </div>
              <div className="h-[400px]">
                <RealTimeAlertsPanel />
              </div>
            </div>
          )}

          {/* Footer Stats */}
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6 text-center">
              <div>
                <div className="text-2xl font-bold text-primary">127</div>
                <div className="text-sm text-muted-foreground">Aktive Mitarbeiter</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-success">89%</div>
                <div className="text-sm text-muted-foreground">Abdeckungsrate</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-warning">24</div>
                <div className="text-sm text-muted-foreground">Offene Schichten</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-error">3</div>
                <div className="text-sm text-muted-foreground">Konflikte</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-accent">156h</div>
                <div className="text-sm text-muted-foreground">Überstunden</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-secondary">98.5%</div>
                <div className="text-sm text-muted-foreground">System-Verfügbarkeit</div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminScheduleManagementDashboard;